<template>
  <b-nav-item
    v-if="enabled"
    class="chat-badge mr-1"
    :title="$t('chat.title')"
    @click="open"
  >
    <feather-icon
      icon="MessageCircleIcon"
      size="21"
    />
    <span
      v-if="unread"
      class="chat-badge__count"
    >{{ unread > 99 ? '99+' : unread }}</span>
  </b-nav-item>
</template>

<script>
import { messagePreviewKey } from '@/utils/chat'
import ChatAvatar from '@/components/chat/ChatAvatar.vue'
import {
  playBeep, showBrowserNotification, windowAway, alertsEnabled,
} from '@/utils/chatNotify'

const TITLE_PREFIX = /^\(\d+\) /

export default {
  name: 'ChatBadge',

  computed: {
    enabled() {
      return !!this.$store.state.user.id && this.$hasAbility('chat.usar')
    },
    nodeId() {
      const node = this.$store.state.selected_node

      return node ? node.id : null
    },
    unread() {
      return this.$store.getters['chat/unreadTotal']
    },
    incoming() {
      return this.$store.state.chat.incoming
    },
  },

  watch: {
    enabled: {
      immediate: true,
      handler(on) {
        if (on) this.$store.dispatch('chat/start')
      },
    },
    // Trocou de nó: os contatos e o canal de presença são outros.
    nodeId(next, previous) {
      if (!this.enabled || next === previous) return
      this.$store.dispatch('chat/stop')
      this.$store.dispatch('chat/start')
    },
    incoming(next) {
      if (next) this.notify(next)
    },
    unread: {
      immediate: true,
      handler(count) {
        const base = document.title.replace(TITLE_PREFIX, '')
        document.title = count ? `(${count}) ${base}` : base
      },
    },
  },

  methods: {
    open() {
      if (this.$route.name !== 'chat') this.$router.push({ name: 'chat' })
    },

    async openConversation(id) {
      if (this.$route.name !== 'chat') await this.$router.push({ name: 'chat' }).catch(() => {})
      this.$store.dispatch('chat/select', id)
    },

    previewOf(message) {
      const key = messagePreviewKey(message)

      return key ? this.$t(key) : message.body
    },

    /**
     * Mensagem chegou e a conversa não está à vista. Janela fora de vista (minimizada, em outra aba ou atrás
     * de outro programa) → notificação do sistema; à vista em outra tela → toast (dentro do chat, o
     * contador da lista já avisa).
     */
    notify({
      conversationId, peerName, peerPhoto, message,
    }) {
      playBeep()
      if (!alertsEnabled()) return
      const title = peerName || this.$t('chat.title')
      const body = this.previewOf(message)
      const open = () => this.openConversation(conversationId)

      if (windowAway()) {
        const shown = showBrowserNotification({
          title, body, tag: `chat-${conversationId}`, icon: peerPhoto, onClick: open,
        })
        // Sem permissão da notificação nativa o toast ainda vale, desde que a página esteja visível.
        if (shown || document.visibilityState !== 'visible') return
      }
      if (this.$route.name === 'chat') return

      const id = `chat-toast-${conversationId}`
      // Uma conversa, um toast: a mensagem nova substitui a anterior em vez de empilhar.
      this.$bvToast.hide(id)

      const h = this.$createElement
      this.$bvToast.toast([h('div', {
        class: 'chat-toast',
        attrs: { role: 'button', tabindex: 0 },
        on: {
          click: () => { this.$bvToast.hide(id); open() },
          keyup: e => { if (e.key === 'Enter') { this.$bvToast.hide(id); open() } },
        },
      }, [
        h(ChatAvatar, { props: { name: title, photo: peerPhoto, size: 42 } }),
        h('div', { class: 'chat-toast__text' }, [
          h('strong', { class: 'chat-toast__name' }, title),
          h('span', { class: 'chat-toast__body' }, body),
        ]),
        h('button', {
          class: 'chat-toast__close',
          attrs: { type: 'button', 'aria-label': this.$t('chat.dismiss') },
          on: { click: e => { e.stopPropagation(); this.$bvToast.hide(id) } },
        }, '×'),
      ])], {
        id,
        toaster: 'b-toaster-bottom-right',
        solid: true,
        noCloseButton: true,
        autoHideDelay: 6000,
        bodyClass: 'p-0',
        toastClass: 'chat-toast-wrap',
      })
    },
  },
}
</script>

<style scoped>
.chat-badge { position: relative; }

.chat-badge__count {
  position: absolute;
  top: 2px;
  right: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ea5455;
  box-shadow: 0 0 0 2px var(--chat-badge-ring, #fff);
  color: #fff;
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  animation: chat-badge-pop 0.25s ease;
}

.dark-layout .chat-badge__count { --chat-badge-ring: #283046; }

@keyframes chat-badge-pop { from { transform: scale(0.4); } }

</style>

<!-- O toast é renderizado fora deste componente (no toaster do body), então o estilo precisa ser global. -->
<style>
.chat-toast-wrap {
  max-width: 360px;
  border: 0;
  background: transparent;
  box-shadow: none;
}

.chat-toast-wrap .toast-body { padding: 0; }

.chat-toast {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 340px;
  max-width: 100%;
  padding: 12px 36px 12px 16px;
  overflow: hidden;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(34, 41, 47, 0.18);
  color: #3b3a4a;
  cursor: pointer;
  animation: chat-toast-in 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  transition: transform 0.15s, box-shadow 0.15s;
}

.chat-toast::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 4px;
  background: #7367f0;
}

.chat-toast:hover { transform: translateY(-2px); box-shadow: 0 16px 36px rgba(34, 41, 47, 0.24); }
.chat-toast:focus-visible { outline: 2px solid #7367f0; outline-offset: 2px; }

.chat-toast__text { display: flex; min-width: 0; flex-direction: column; gap: 2px; }
.chat-toast__name { overflow: hidden; font-size: 0.95rem; text-overflow: ellipsis; white-space: nowrap; }

.chat-toast__body {
  display: -webkit-box;
  overflow: hidden;
  color: #6e6b7b;
  font-size: 0.86rem;
  line-height: 1.3;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.chat-toast__close {
  position: absolute;
  top: 6px;
  right: 8px;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #b9b9c3;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
}

.chat-toast__close:hover { background: rgba(115, 103, 240, 0.12); color: #7367f0; }

.dark-layout .chat-toast { border-color: #3b4253; background: #283046; color: #d0d2d6; }
.dark-layout .chat-toast__body { color: #b4b7bd; }

@keyframes chat-toast-in { from { opacity: 0; transform: translateY(14px) scale(0.98); } }

@media (max-width: 575px) {
  .b-toaster.b-toaster-bottom-right { right: 8px; left: 8px; }
  .chat-toast { width: 100%; }
}
</style>
